import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ApiError,
  organizationsApi,
  type CreateInvitationPayload,
  type InvitationDTO,
  type MemberItemDTO,
} from '@/shared/api'

type Role = NonNullable<CreateInvitationPayload['role']>

const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: 'owner', label: 'Owner — controle total' },
  { value: 'admin', label: 'Admin — gerencia membros e dados' },
  { value: 'member', label: 'Member — usa o chat e faz upload' },
  { value: 'viewer', label: 'Viewer — apenas leitura' },
  { value: 'auditor', label: 'Auditor — leitura org-wide' },
]

interface Props {
  /** Role of the currently authenticated user. */
  currentRole: string | null
}

function errorDetail(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.detail || fallback
  if (err instanceof Error) return err.message
  return fallback
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

/**
 * Members + invitations panel. Hidden for viewer/auditor — those roles can
 * see member counts elsewhere but should never get write affordances here.
 */
export function MembersSection({ currentRole }: Props) {
  const canManage = currentRole === 'owner' || currentRole === 'admin'
  const canPromoteOwner = currentRole === 'owner'

  const [members, setMembers] = useState<MemberItemDTO[]>([])
  const [invitations, setInvitations] = useState<InvitationDTO[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<Role>('member')
  const [inviteSubmitting, setInviteSubmitting] = useState(false)
  const [newInvite, setNewInvite] = useState<{
    invitation: InvitationDTO
    token: string
    acceptPath: string
  } | null>(null)
  const [inviteError, setInviteError] = useState<string | null>(null)
  const [tokenCopied, setTokenCopied] = useState(false)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [m, i] = await Promise.all([
        organizationsApi.listMembers(),
        canManage
          ? organizationsApi.listInvitations()
          : Promise.resolve({ items: [], organization_id: '' as any, total: 0 }),
      ])
      setMembers(m.items)
      setInvitations(i.items)
    } catch (err) {
      setError(errorDetail(err, 'Falha ao carregar membros.'))
    } finally {
      setLoading(false)
    }
  }, [canManage])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const handleInvite = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()
      if (inviteSubmitting) return
      const cleaned = inviteEmail.trim().toLowerCase()
      if (!cleaned) {
        setInviteError('Informe o email.')
        return
      }
      setInviteSubmitting(true)
      setInviteError(null)
      setTokenCopied(false)
      try {
        const response = await organizationsApi.createInvitation({
          email: cleaned,
          role: inviteRole,
        })
        setNewInvite({
          invitation: response.invitation,
          token: response.token,
          acceptPath: response.accept_path,
        })
        setInviteEmail('')
        await refresh()
      } catch (err) {
        setInviteError(errorDetail(err, 'Falha ao criar convite.'))
      } finally {
        setInviteSubmitting(false)
      }
    },
    [inviteEmail, inviteRole, inviteSubmitting, refresh],
  )

  const copyToken = useCallback(async () => {
    if (!newInvite) return
    try {
      await navigator.clipboard.writeText(newInvite.token)
      setTokenCopied(true)
      setTimeout(() => setTokenCopied(false), 2000)
    } catch {
      // ignored
    }
  }, [newInvite])

  const handleRoleChange = useCallback(
    async (member: MemberItemDTO, role: Role) => {
      try {
        await organizationsApi.updateMember(member.id, { role })
        await refresh()
      } catch (err) {
        setError(errorDetail(err, 'Falha ao atualizar membro.'))
      }
    },
    [refresh],
  )

  const handleRemove = useCallback(
    async (member: MemberItemDTO) => {
      if (!window.confirm(`Remover ${member.full_name || member.email}?`)) return
      try {
        await organizationsApi.removeMember(member.id)
        await refresh()
      } catch (err) {
        setError(errorDetail(err, 'Falha ao remover membro.'))
      }
    },
    [refresh],
  )

  const handleRevokeInvite = useCallback(
    async (inv: InvitationDTO) => {
      if (!window.confirm(`Revogar convite para ${inv.email}?`)) return
      try {
        await organizationsApi.revokeInvitation(inv.id)
        await refresh()
      } catch (err) {
        setError(errorDetail(err, 'Falha ao revogar convite.'))
      }
    },
    [refresh],
  )

  const sortedMembers = useMemo(
    () =>
      [...members].sort((a, b) => {
        const order: Record<string, number> = {
          owner: 0,
          admin: 1,
          member: 2,
          viewer: 3,
          auditor: 4,
        }
        return (
          (order[a.role] ?? 99) - (order[b.role] ?? 99) ||
          a.full_name.localeCompare(b.full_name)
        )
      }),
    [members],
  )

  return (
    <div className="settings-section">
      <header className="settings-section-head">
        <h3>Membros</h3>
        <p className="settings-section-help">
          Gerencie quem tem acesso ao workspace. Owner/admin podem convidar e
          alterar papéis. Apenas owners promovem outros owners.
        </p>
      </header>

      {error ? (
        <div className="settings-alert is-error" role="alert">
          {error}
        </div>
      ) : null}

      {canManage ? (
        <form className="settings-invite-form" onSubmit={(e) => void handleInvite(e)}>
          <div className="settings-invite-row">
            <input
              type="email"
              data-testid="invite-email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="email@convidado.com"
              autoComplete="off"
              disabled={inviteSubmitting}
              className="settings-input"
            />
            <select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as Role)}
              disabled={inviteSubmitting}
              className="settings-input"
            >
              {ROLE_OPTIONS.filter(
                (opt) => opt.value !== 'owner' || canPromoteOwner,
              ).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <button
              type="submit"
              className="settings-button is-primary"
              data-testid="invite-submit"
              disabled={inviteSubmitting}
            >
              {inviteSubmitting ? 'Enviando…' : 'Convidar'}
            </button>
          </div>
          {inviteError ? (
            <p className="settings-field-error">{inviteError}</p>
          ) : null}
        </form>
      ) : null}

      {newInvite ? (
        <div className="settings-alert is-success" role="status" data-testid="invite-success">
          <p>
            Convite criado para <strong>{newInvite.invitation.email}</strong> ·{' '}
            {ROLE_OPTIONS.find((r) => r.value === newInvite.invitation.role)
              ?.label ?? newInvite.invitation.role}
          </p>
          <p className="settings-token-warning">
            Copie o token abaixo agora — ele não será exibido novamente.
          </p>
          <div className="settings-token-row">
            <code className="settings-token" data-testid="invite-token">{newInvite.token}</code>
            <button
              type="button"
              className="settings-button"
              onClick={() => void copyToken()}
            >
              {tokenCopied ? 'Copiado!' : 'Copiar token'}
            </button>
          </div>
          <p className="settings-token-hint">
            URL de aceite: <code>{newInvite.acceptPath}</code>
          </p>
        </div>
      ) : null}

      <div className="settings-list">
        <header className="settings-list-head">
          <h4>Pessoas com acesso</h4>
          <button
            type="button"
            className="settings-button"
            onClick={() => void refresh()}
            disabled={loading}
          >
            {loading ? 'Carregando…' : 'Atualizar'}
          </button>
        </header>
        {sortedMembers.length === 0 ? (
          <p className="settings-empty">Nenhum membro encontrado.</p>
        ) : (
          <ul className="settings-member-list">
            {sortedMembers.map((m) => (
              <li key={m.id} className="settings-member-row">
                <div className="settings-member-info">
                  <strong className="settings-member-name">
                    {m.full_name || m.email}
                  </strong>
                  <span className="settings-member-email">{m.email}</span>
                  <span className="settings-member-meta">
                    Status: {m.status} · Desde {formatDateTime(m.created_at)}
                  </span>
                </div>
                <div className="settings-member-actions">
                  {canManage ? (
                    <select
                      value={m.role}
                      onChange={(e) =>
                        void handleRoleChange(m, e.target.value as Role)
                      }
                      className="settings-input"
                    >
                      {ROLE_OPTIONS.filter(
                        (opt) => opt.value !== 'owner' || canPromoteOwner,
                      ).map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label.split(' — ')[0]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="settings-member-role">{m.role}</span>
                  )}
                  {canManage && m.role !== 'owner' ? (
                    <button
                      type="button"
                      className="settings-button is-danger"
                      onClick={() => void handleRemove(m)}
                    >
                      Remover
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {canManage && invitations.length > 0 ? (
        <div className="settings-list">
          <header className="settings-list-head">
            <h4>Convites pendentes</h4>
          </header>
          <ul className="settings-member-list">
            {invitations.map((inv) => (
              <li key={inv.id} className="settings-member-row">
                <div className="settings-member-info">
                  <strong className="settings-member-name">{inv.email}</strong>
                  <span className="settings-member-meta">
                    Papel: {inv.role} · Expira em {formatDateTime(inv.expires_at)}
                  </span>
                </div>
                <div className="settings-member-actions">
                  <button
                    type="button"
                    className="settings-button is-danger"
                    onClick={() => void handleRevokeInvite(inv)}
                  >
                    Revogar
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}
